import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { RelocationStatus } from "@/gql/graphql";
import { ArchiveBoxIcon, PencilIcon } from "@heroicons/react/24/outline";
import { CopyIcon, GitBranchPlus, Megaphone, Star, Truck } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowTopRightOnSquareIcon, PlayIcon } from "@heroicons/react/20/solid";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { RelocationEditDialog } from "@/app/Relocations/Components/RelocationEditDialog";
import { toast } from "sonner";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  duplicateRelocationMutation,
  setRelocationPriorityMutation,
  updateRelocationStatuses,
} from "@/app/Relocations/GraphQL/relocationMutations";
import { PromoteRelocationDialog } from "@/app/Promotions/Components/PromoteRelocationDialog";
import { RelocationListItem } from "@/app/Relocations/GraphQL/relocationListQuery";
import { AddDestinationsDialog } from "@/app/Relocations/Components/AddDestinationsDialog";
import { useTranslations } from "use-intl";
import { CreateWorkOrderDialog } from "@/app/ProDrivers/WorkOrders/Components/CreateWorkOrderDialog";
import { RelocationRecord } from "@/app/Relocations/GraphQL/relocationRecordQuery";

export function useRelocationActions(): ActionsHook<
  RelocationListItem | RelocationRecord
> {
  const { mutateAsync } = useGqlMutation(updateRelocationStatuses);
  const { mutateAsync: duplicate } = useGqlMutation(
    duplicateRelocationMutation,
  );
  const { mutateAsync: setPriority } = useGqlMutation(
    setRelocationPriorityMutation,
  );
  const { open: openPromoteDialog } = useDialog(PromoteRelocationDialog);
  const navigate = useNavigate();
  const { open } = useDialog(RelocationEditDialog);
  const { open: openDropoffDialog } = useDialog(AddDestinationsDialog);
  const { open: openWorkOrderDialog } = useDialog(CreateWorkOrderDialog);
  const t = useTranslations("relocation.actions");

  return (relocation) => {
    return [
      {
        label: t("label"),
        items: [
          {
            actions: [
              {
                label: t("ready"),
                Icon: PlayIcon,
                color: "green",
                isHidden: relocation.status !== RelocationStatus.Draft,
                handleAction: () => {
                  return mutateAsync({
                    ids: [relocation.id],
                    status: RelocationStatus.Ready,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: t("view"),
                Icon: ArrowTopRightOnSquareIcon,
                handleAction: async () => {
                  // customer_url is the canonical deal URL, built backend-side
                  // so admin can't drift from the marketplace route shape.
                  // preview=true so non-READY deals (archived/expired/etc.)
                  // render instead of 301-ing to the corridor listing.
                  const url = new URL(relocation.customer_url);
                  url.searchParams.set("preview", "true");
                  window.open(url, "_blank");
                },
              },
            ],
          },
          {
            actions: [
              {
                label: t("edit"),
                isHiddenTopBar: true,
                Icon: PencilIcon,
                handleAction: () => {
                  open({
                    id: relocation.id,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: t("addDestinations"),
                Icon: GitBranchPlus,
                isHidden:
                  relocation.is_linked ||
                  relocation.status === RelocationStatus.Archived,
                handleAction: async () => {
                  openDropoffDialog({
                    relocationId: relocation.id,
                  });
                },
              },
              {
                label: t("duplicate"),
                Icon: CopyIcon,
                handleAction: async () => {
                  const res = await duplicate({
                    id: relocation.id,
                  });

                  toast.success(t("duplicatedSuccess"), {
                    action: {
                      label: t("view"),
                      onClick: () => {
                        navigate({
                          from: "/$tenant/$orgId/supplier/{-$supplierId}",
                          to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId",
                          params: (prev) => ({
                            ...prev,
                            relocationId: res.duplicateRelocation.id,
                          }),
                        });
                      },
                    },
                  });
                },
              },
              {
                label: t("setPriority"),
                Icon: Star,
                isAdminAction: true,
                isHidden:
                  relocation.priority !== null &&
                  relocation.priority !== undefined,
                handleAction: async () => {
                  await setPriority({
                    id: relocation.id,
                    priority: 0,
                  });

                  toast.success(t("prioritySetSuccess"));
                },
              },
              {
                label: t("promote"),
                Icon: Megaphone,
                isAdminAction: true,
                handleAction: () => {
                  openPromoteDialog({ relocationId: relocation.id });
                },
              },
              {
                label: t("removePriority"),
                Icon: Star,
                isAdminAction: true,
                isHidden:
                  relocation.priority === null ||
                  relocation.priority === undefined,
                handleAction: async () => {
                  await setPriority({
                    id: relocation.id,
                    priority: null,
                  });

                  toast.success(t("priorityRemovedSuccess"));
                },
              },
            ],
          },
          {
            actions: [
              {
                label: t("requestProDriver"),
                Icon: Truck,
                handleAction: async () => {
                  const rec = relocation as RelocationRecord;
                  const departureAddress = rec.departureOffice?.address;
                  const deliveryAddress = rec.deliveryOffice?.address;

                  openWorkOrderDialog({
                    initialValues: {
                      supplier: { connect: relocation.supplier.id },
                      vehicle_type: relocation.vehicle.type,
                      currency: relocation.currency,
                      relocation: { connect: relocation.id },
                      depart_after: rec.available_from_date,
                      deliver_before: rec.available_to_date,
                      ...(departureAddress
                        ? {
                            departureAddress: {
                              upsert: {
                                address_line_1:
                                  departureAddress.address_line_1 ?? undefined,
                                address_line_2:
                                  departureAddress.address_line_2 ?? undefined,
                                city: departureAddress.city ?? undefined,
                                state: departureAddress.state ?? undefined,
                                country: departureAddress.country ?? undefined,
                                postcode:
                                  departureAddress.postcode ?? undefined,
                              },
                            },
                          }
                        : {}),
                      ...(deliveryAddress
                        ? {
                            deliveryAddress: {
                              upsert: {
                                address_line_1:
                                  deliveryAddress.address_line_1 ?? undefined,
                                address_line_2:
                                  deliveryAddress.address_line_2 ?? undefined,
                                city: deliveryAddress.city ?? undefined,
                                state: deliveryAddress.state ?? undefined,
                                country: deliveryAddress.country ?? undefined,
                                postcode: deliveryAddress.postcode ?? undefined,
                              },
                            },
                          }
                        : {}),
                    },
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                isHidden: relocation.status !== RelocationStatus.Ready,
                label: t("archive"),
                color: "purple",
                Icon: ArchiveBoxIcon,
                handleAction: async () => {
                  await mutateAsync({
                    ids: [relocation.id],
                    status: RelocationStatus.Archived,
                  });
                },
              },
            ],
          },
        ],
      },
    ];
  };
}
